import { useState, useRef, useEffect } from 'react';
import { ChevronRight, ChevronLeft, ChevronDown, Check, Info, Phone, Smartphone, Battery, Camera, PlugZap, Wrench, Square, CircleDot, Layers } from 'lucide-react';
import { toast } from 'sonner';

/* ================================================================
   PRICING DATA — grille tarifaire Excel (par modèle, par réparation)
   screenFH = Écran Full HD | screenOLED = Écran Soft OLED
   battery  = Batterie seule | batteryCombo = Batterie + écran (prix grille)
   vitre    = Vitre arrière seule | vitreCombo = Vitre + écran (prix grille)
   cdc = Connecteur de charge | camera = Caméra arrière
   lentille = Lentille / hublot caméra

   Packs (quand l'écran est réparé en même temps) : on applique le prix
   « avec écran » EXACT de la grille (basé Full HD). En écran OLED, on ajoute
   le supplément OLED (screenOLED − screenFH) au prix du pack.
   ================================================================ */

const IPHONE_PRICES = {
  'iPhone 8 / 8 Plus / SE2 / SE3': { screenFH: 45, screenOLED: null, battery: 25, batteryCombo: 55, vitre: 60, vitreCombo: 85, cdc: 45, camera: 45, lentille: 25 },
  'iPhone X / XS':                 { screenFH: 55, screenOLED: 69, battery: 45, batteryCombo: 80, vitre: 65, vitreCombo: 110, cdc: 45, camera: 45, lentille: 25 },
  'iPhone XS Max':                 { screenFH: 65, screenOLED: 95, battery: 50, batteryCombo: 80, vitre: 70, vitreCombo: 120, cdc: 45, camera: 45, lentille: 25 },
  'iPhone XR':                     { screenFH: 65, screenOLED: 99, battery: 45, batteryCombo: 95, vitre: 80, vitreCombo: 125, cdc: 49, camera: 49, lentille: 25 },
  'iPhone 11':                     { screenFH: 65, screenOLED: 99, battery: 45, batteryCombo: 95, vitre: 80, vitreCombo: 125, cdc: 49, camera: 49, lentille: 25 },
  'iPhone 11 Pro':                 { screenFH: 75, screenOLED: 120, battery: 55, batteryCombo: 115, vitre: 85, vitreCombo: 145, cdc: 59, camera: 69, lentille: 25 },
  'iPhone 11 Pro Max':             { screenFH: 85, screenOLED: 120, battery: 55, batteryCombo: 115, vitre: 90, vitreCombo: 155, cdc: 69, camera: 79, lentille: 25 },
  'iPhone 12 / 12 Pro':            { screenFH: 85, screenOLED: 125, battery: 60, batteryCombo: 120, vitre: 95, vitreCombo: 150, cdc: 59, camera: 59, lentille: 25 },
  'iPhone 12 Mini':                { screenFH: 85, screenOLED: 120, battery: 60, batteryCombo: 125, vitre: 90, vitreCombo: 155, cdc: 59, camera: 89, lentille: 25 },
  'iPhone 12 Pro Max':             { screenFH: 90, screenOLED: 135, battery: 60, batteryCombo: 140, vitre: 95, vitreCombo: 170, cdc: 59, camera: 109, lentille: 25 },
  'iPhone 13':                     { screenFH: 95, screenOLED: 125, battery: 65, batteryCombo: 140, vitre: 100, vitreCombo: 175, cdc: 69, camera: 59, lentille: 25 },
  'iPhone 13 Mini':                { screenFH: 95, screenOLED: 165, battery: 65, batteryCombo: 150, vitre: 100, vitreCombo: 180, cdc: 49, camera: 89, lentille: 25 },
  'iPhone 13 Pro':                 { screenFH: 120, screenOLED: 165, battery: 65, batteryCombo: 175, vitre: 110, vitreCombo: 210, cdc: 59, camera: 119, lentille: 25 },
  'iPhone 13 Pro Max':             { screenFH: 130, screenOLED: 175, battery: 60, batteryCombo: 165, vitre: 95, vitreCombo: 205, cdc: 69, camera: 119, lentille: 25 },
  'iPhone 14':                     { screenFH: 99, screenOLED: 135, battery: 70, batteryCombo: 165, vitre: 95, vitreCombo: 175, cdc: 69, camera: 89, lentille: 45 },
  'iPhone 14 Pro':                 { screenFH: 109, screenOLED: 149, battery: 80, batteryCombo: 175, vitre: 135, vitreCombo: 220, cdc: 69, camera: 89, lentille: 25 },
  'iPhone 14 Pro Max':             { screenFH: 119, screenOLED: 159, battery: 85, batteryCombo: 190, vitre: 140, vitreCombo: 235, cdc: 69, camera: 99, lentille: 25 },
  'iPhone 14 Plus':                { screenFH: 99, screenOLED: 150, battery: 75, batteryCombo: 160, vitre: 90, vitreCombo: 170, cdc: 79, camera: 79, lentille: 45 },
  'iPhone 15':                     { screenFH: 99, screenOLED: 175, battery: 80, batteryCombo: 165, vitre: 99, vitreCombo: 180, cdc: 69, camera: 89, lentille: 45 },
  'iPhone 15 Pro':                 { screenFH: 110, screenOLED: 155, battery: 90, batteryCombo: 180, vitre: 110, vitreCombo: 200, cdc: 79, camera: 99, lentille: 45 },
  'iPhone 15 Pro Max':             { screenFH: 120, screenOLED: 195, battery: 99, batteryCombo: 200, vitre: 115, vitreCombo: 215, cdc: 79, camera: 109, lentille: 45 },
  'iPhone 15 Plus':                { screenFH: 99, screenOLED: 180, battery: 85, batteryCombo: 170, vitre: 99, vitreCombo: 185, cdc: 69, camera: 89, lentille: 45 },
  'iPhone 16':                     { screenFH: 149, screenOLED: 190, battery: 80, batteryCombo: 210, vitre: 99, vitreCombo: 230, cdc: 79, camera: 99, lentille: 45 },
  'iPhone 16 Pro':                 { screenFH: 180, screenOLED: 240, battery: 89, batteryCombo: 250, vitre: 110, vitreCombo: 270, cdc: 69, camera: 109, lentille: 45 },
  'iPhone 16 Pro Max':             { screenFH: 195, screenOLED: 249, battery: 99, batteryCombo: 275, vitre: 120, vitreCombo: 290, cdc: 79, camera: 109, lentille: 45 },
  'iPhone 16e':                    { screenFH: 109, screenOLED: 130, battery: 80, batteryCombo: 165, vitre: 119, vitreCombo: 200, cdc: 79, camera: 99, lentille: 45 },
  'iPhone 16 Plus':                { screenFH: 109, screenOLED: 209, battery: 85, batteryCombo: 180, vitre: 109, vitreCombo: 190, cdc: 59, camera: 99, lentille: 45 },
  'iPhone 17':                     { screenFH: 159, screenOLED: 199, battery: 80, batteryCombo: 224, vitre: 149, vitreCombo: 290, cdc: 130, camera: 140, lentille: 45 },
  'iPhone 17 Pro':                 { screenFH: 199, screenOLED: 229, battery: 85, batteryCombo: 259, vitre: 149, vitreCombo: 330, cdc: 130, camera: 140, lentille: 45 },
  'iPhone 17 Pro Max':             { screenFH: 209, screenOLED: 259, battery: 90, batteryCombo: 274, vitre: 169, vitreCombo: 355, cdc: 140, camera: 140, lentille: 45 },
};

/* ---------- Model groups for the UI ---------- */

const IPHONE_GROUPS = [
  { key: '17', label: 'iPhone 17', variants: ['iPhone 17 Pro Max', 'iPhone 17 Pro', 'iPhone 17'] },
  { key: '16', label: 'iPhone 16', variants: ['iPhone 16 Pro Max', 'iPhone 16 Pro', 'iPhone 16 Plus', 'iPhone 16', 'iPhone 16e'] },
  { key: '15', label: 'iPhone 15', variants: ['iPhone 15 Pro Max', 'iPhone 15 Pro', 'iPhone 15 Plus', 'iPhone 15'] },
  { key: '14', label: 'iPhone 14', variants: ['iPhone 14 Pro Max', 'iPhone 14 Pro', 'iPhone 14 Plus', 'iPhone 14'] },
  { key: '13', label: 'iPhone 13', variants: ['iPhone 13 Pro Max', 'iPhone 13 Pro', 'iPhone 13', 'iPhone 13 Mini'] },
  { key: '12', label: 'iPhone 12', variants: ['iPhone 12 Pro Max', 'iPhone 12 / 12 Pro', 'iPhone 12 Mini'] },
  { key: '11', label: 'iPhone 11', variants: ['iPhone 11 Pro Max', 'iPhone 11 Pro', 'iPhone 11'] },
  { key: 'x', label: 'iPhone X / XR', variants: ['iPhone XS Max', 'iPhone X / XS', 'iPhone XR'] },
  { key: 'se', label: 'iPhone 8 / SE', variants: ['iPhone 8 / 8 Plus / SE2 / SE3'] },
  { key: 'other', label: 'Autre', variants: null },
];

/* ---------- Samsung data (unchanged) ---------- */

const BRANDS = [
  { key: 'apple', label: 'Apple' },
  { key: 'samsung', label: 'Samsung' },
  { key: 'other', label: 'Autre marque' },
];

const SAMSUNG_GROUPS = [
  { key: 'S', label: 'Galaxy S', variants: ['Galaxy S26', 'Galaxy S25', 'Galaxy S24', 'Galaxy S23', 'Galaxy S22', 'Galaxy S21', 'Galaxy S20'] },
  { key: 'A', label: 'Galaxy A (5G)', variants: ['Galaxy A56 5G', 'Galaxy A55 5G', 'Galaxy A54 5G', 'Galaxy A53 5G', 'Galaxy A52 5G', 'Galaxy A51 5G', 'Galaxy A42 5G', 'Galaxy A37 5G', 'Galaxy A36 5G', 'Galaxy A35 5G', 'Galaxy A34 5G', 'Galaxy A33 5G', 'Galaxy A32 5G', 'Galaxy A26 5G', 'Galaxy A25 5G', 'Galaxy A23 5G', 'Galaxy A22 5G', 'Galaxy A17 5G', 'Galaxy A16 5G', 'Galaxy A15 5G', 'Galaxy A14 5G', 'Galaxy A13 5G', 'Galaxy A06 5G'] },
  { key: 'Flip', label: 'Galaxy Z Flip', variants: ['Galaxy Z Flip 6', 'Galaxy Z Flip 5', 'Galaxy Z Flip 4', 'Galaxy Z Flip 3'] },
  { key: 'Fold', label: 'Galaxy Z Fold', variants: ['Galaxy Z Fold 6', 'Galaxy Z Fold 5', 'Galaxy Z Fold 4', 'Galaxy Z Fold 3'] },
  { key: 'other', label: 'Autre', variants: null },
];

const SAMSUNG_PRICES = {
  'Galaxy A06 5G': { battery: 65 },
  'Galaxy A13 5G': { battery: 65, screen: 77 },
  'Galaxy A14 5G': { battery: 65, screen: 80 },
  'Galaxy A15 5G': { battery: 65, screen: 80 },
  'Galaxy A16 5G': { battery: 65, screen: 91 },
  'Galaxy A17 5G': { battery: 65, screen: 90 },
  'Galaxy A22 5G': { battery: 65, screen: 85 },
  'Galaxy A23 5G': { battery: 65, screen: 85 },
  'Galaxy A25 5G': { battery: 65, screen: 90 },
  'Galaxy A26 5G': { battery: 65, screen: 90 },
  'Galaxy A32 5G': { battery: 65, screen: 90 },
  'Galaxy A33 5G': { battery: 65, screen: 110 },
  'Galaxy A34 5G': { battery: 65, screen: 115 },
  'Galaxy A35 5G': { battery: 65, screen: 95 },
  'Galaxy A36 5G': { battery: 65, screen: 115 },
  'Galaxy A37 5G': { battery: 65, screen: 120 },
  'Galaxy A42 5G': { battery: 65, screen: 90 },
  'Galaxy A51 5G': { battery: 65, screen: 125 },
  'Galaxy A52 5G': { battery: 65, screen: 120 },
  'Galaxy A53 5G': { battery: 65, screen: 120 },
  'Galaxy A54 5G': { battery: 65, screen: 120 },
  'Galaxy A55 5G': { battery: 65, screen: 95 },
  'Galaxy A56 5G': { battery: 65, screen: 100 },
  'Galaxy S20': { battery: 65, screen: 210 },
  'Galaxy S21': { battery: 65, screen: 175 },
  'Galaxy S22': { battery: 65, screen: 180 },
  'Galaxy S23': { battery: 65, screen: 180 },
  'Galaxy S24': { battery: 65, screen: 185 },
  'Galaxy S25': { battery: 65, screen: 155 },
  'Galaxy S26': { battery: 65, screen: 185 },
  'Galaxy Z Flip 3': { battery: 65, screen: 295 },
  'Galaxy Z Flip 4': { battery: 65, screen: 300 },
  'Galaxy Z Flip 5': { battery: 65, screen: 290 },
  'Galaxy Z Flip 6': { battery: 65, screen: 260 },
  'Galaxy Z Fold 3': { battery: 65, screen: 160, screen_ext: 460 },
  'Galaxy Z Fold 4': { battery: 65, screen: 120, screen_ext: 425 },
  'Galaxy Z Fold 5': { battery: 65, screen: 120, screen_ext: 530 },
  'Galaxy Z Fold 6': { battery: 65, screen: 130, screen_ext: 565 },
};

const buildSamsungIssues = (model) => {
  const prices = SAMSUNG_PRICES[model];
  const list = [];
  if (prices?.screen) list.push({ id: 'screen', label: 'Écran', price: prices.screen, Icon: Smartphone });
  if (prices?.screen_ext) list.push({ id: 'screen_ext', label: 'Écran extérieur', price: prices.screen_ext, Icon: Square });
  if (prices?.battery) list.push({ id: 'battery', label: 'Batterie', price: prices.battery, Icon: Battery });
  list.push({ id: 'other', label: 'Autre panne (sur devis)', price: null, Icon: Wrench });
  return list;
};

/* ---------- Apple repair options ---------- */

const APPLE_REPAIR_TYPES = [
  { id: 'screen',   label: 'Écran',              Icon: Smartphone, key: 'screen' },
  { id: 'battery',  label: 'Batterie',           Icon: Battery,    key: 'battery' },
  { id: 'vitre',    label: 'Vitre arrière',      Icon: Square,     key: 'vitre' },
  { id: 'cdc',      label: 'Connecteur de charge', Icon: PlugZap,  key: 'cdc' },
  { id: 'camera',   label: 'Caméra arrière',     Icon: Camera,     key: 'camera' },
  { id: 'lentille', label: 'Lentille caméra',    Icon: CircleDot,  key: 'lentille' },
  { id: 'other',    label: 'Autre panne',        Icon: Wrench,     key: null },
];

/* ---------- Price calculation ---------- */

function calcAppleTotal(model, selectedRepairs, screenType) {
  const p = IPHONE_PRICES[model];
  if (!p) return { total: 0, lines: [], hasCustom: false };

  const has = (id) => selectedRepairs.includes(id);
  const screenPrice = screenType === 'oled' && p.screenOLED ? p.screenOLED : p.screenFH;
  // Les prix « pack » de la grille sont basés Full HD ; en OLED on ajoute le supplément.
  const oledSurcharge = screenType === 'oled' && p.screenOLED ? p.screenOLED - p.screenFH : 0;

  let total = 0;
  const lines = [];
  let screenHandled = false;
  let batteryHandled = false;
  let vitreHandled = false;
  let hasCustom = false;

  // Pack Écran + Batterie → prix « avec écran » de la grille (+ supplément OLED)
  if (has('screen') && has('battery')) {
    const combo = p.batteryCombo != null ? p.batteryCombo + oledSurcharge : screenPrice + p.battery;
    total += combo;
    lines.push({ label: `Écran ${screenType === 'oled' ? 'OLED' : 'Full HD'} + Batterie`, price: combo, isCombo: true });
    screenHandled = true;
    batteryHandled = true;
  }

  // Pack Écran + Vitre arrière → prix « avec écran » de la grille (+ supplément OLED), si l'écran n'est pas déjà dans le pack batterie
  if (has('screen') && has('vitre') && !screenHandled) {
    const combo = p.vitreCombo != null ? p.vitreCombo + oledSurcharge : screenPrice + p.vitre;
    total += combo;
    lines.push({ label: `Écran ${screenType === 'oled' ? 'OLED' : 'Full HD'} + Vitre arrière`, price: combo, isCombo: true });
    screenHandled = true;
    vitreHandled = true;
  }

  // Individual screen
  if (has('screen') && !screenHandled) {
    total += screenPrice;
    lines.push({ label: `Écran ${screenType === 'oled' ? 'OLED' : 'Full HD'}`, price: screenPrice });
    screenHandled = true;
  }

  // Individual battery
  if (has('battery') && !batteryHandled) {
    total += p.battery;
    lines.push({ label: 'Batterie', price: p.battery });
  }

  // Individual vitre
  if (has('vitre') && !vitreHandled) {
    total += p.vitre;
    lines.push({ label: 'Vitre arrière', price: p.vitre });
  }

  // CDC
  if (has('cdc')) {
    total += p.cdc;
    lines.push({ label: 'Connecteur de charge', price: p.cdc });
  }

  // Camera
  if (has('camera')) {
    total += p.camera;
    lines.push({ label: 'Caméra arrière', price: p.camera });
  }

  // Lentille
  if (has('lentille')) {
    total += p.lentille;
    lines.push({ label: 'Lentille caméra', price: p.lentille });
  }

  // Other
  if (has('other')) {
    hasCustom = true;
    lines.push({ label: 'Autre panne', price: null });
  }

  return { total, lines, hasCustom };
}

/* ---------- Constants ---------- */

const STEPS = [
  { num: 1, label: 'Modèle' },
  { num: 2, label: 'Panne' },
  { num: 3, label: 'Devis' },
];

const GRADIENT = 'from-blue-500 to-indigo-600';
const BG_GRADIENT = 'from-blue-50 to-indigo-50';

/* ================================================================
   COMPONENT
   ================================================================ */

const QuoteConfigurator = ({ embedded = false }) => {
  const [step, setStep] = useState(1);
  const [selection, setSelection] = useState({
    brand: 'apple',
    model: '',
    issues: [],
    screenType: 'fh', // 'fh' or 'oled'
  });
  const [expandedGroup, setExpandedGroup] = useState(null);
  const containerRef = useRef(null);
  const prevStepRef = useRef(step);
  const heroModelHandled = useRef(false);

  // Pick up model pre-selected from the hero mini-devis
  useEffect(() => {
    const handleHeroModel = () => {
      if (heroModelHandled.current) return;
      const heroModel = window.__alloRepHeroModel;
      if (!heroModel || heroModel === 'Autre') return;
      heroModelHandled.current = true;
      delete window.__alloRepHeroModel;
      // Check if this model exists in IPHONE_PRICES
      if (IPHONE_PRICES[heroModel]) {
        setSelection(s => ({ ...s, brand: 'apple', model: heroModel }));
        setStep(2);
      }
    };
    
    // Check on mount (in case model was set before component mounted)
    handleHeroModel();
    
    // Listen for custom event from HeroSection
    window.addEventListener('allorep:hero-model-selected', handleHeroModel);
    return () => window.removeEventListener('allorep:hero-model-selected', handleHeroModel);
  }, []);

  useEffect(() => {
    if (prevStepRef.current === step) return;
    prevStepRef.current = step;
    const t = setTimeout(() => {
      containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
    return () => clearTimeout(t);
  }, [step]);

  const groups = selection.brand === 'samsung' ? SAMSUNG_GROUPS : IPHONE_GROUPS;

  // Get model prices
  const modelPrices = selection.brand === 'apple' ? IPHONE_PRICES[selection.model] : null;
  const hasOled = modelPrices?.screenOLED != null;

  // Calculate Apple total
  const appleResult = selection.brand === 'apple' && selection.model
    ? calcAppleTotal(selection.model, selection.issues, selection.screenType)
    : { total: 0, lines: [], hasCustom: false };

  // Samsung issues
  const samsungIssues = selection.brand === 'samsung' && selection.model ? buildSamsungIssues(selection.model) : [];
  const samsungSelected = samsungIssues.filter(i => selection.issues.includes(i.id));
  const samsungTotal = samsungSelected.reduce((sum, i) => sum + (i.price ?? 0), 0);
  const samsungHasCustom = samsungSelected.some(i => i.price == null);

  // Unified totals
  const isApple = selection.brand === 'apple';
  const totalPrice = isApple ? appleResult.total : samsungTotal;
  const hasCustomQuote = isApple ? appleResult.hasCustom : samsungHasCustom;
  const isCustomQuote = hasCustomQuote && totalPrice === 0;

  const toggleIssue = (id) => {
    setSelection(s => {
      const newIssues = s.issues.includes(id) ? s.issues.filter(x => x !== id) : [...s.issues, id];
      // If screen is deselected, reset screen type
      if (id === 'screen' && s.issues.includes('screen')) {
        return { ...s, issues: newIssues, screenType: 'fh' };
      }
      return { ...s, issues: newIssues };
    });
  };

  const switchBrand = (brandKey) => {
    setSelection({ brand: brandKey, model: '', issues: [], screenType: 'fh' });
    setExpandedGroup(null);
  };

  const handleNext = () => { if (step < 3) setStep(step + 1); };
  const handleBack = () => { if (step > 1) setStep(step - 1); };

  const canProceed = () => {
    if (selection.brand === 'other') return false;
    switch (step) {
      case 1: return !!selection.model;
      case 2: return selection.issues.length > 0;
      default: return true;
    }
  };

  const brandLabel = BRANDS.find(b => b.key === selection.brand)?.label || '';

  const generateWhatsAppMessage = () => {
    const repairLines = isApple
      ? appleResult.lines.map(l => `- ${l.label}${l.price != null ? ` : ${l.price}€` : ' : sur devis'}${l.isCombo ? ' (pack)' : ''}`)
      : samsungSelected.map(i => `- ${i.label}${i.price != null ? ` : ${i.price}€` : ' : sur devis'}`);

    const priceLine = hasCustomQuote
      ? 'Prix : à confirmer (contient une panne sur devis)'
      : `Prix total : ${totalPrice}€`;

    return encodeURIComponent(
      `Bonjour AlloRéparations,\n\nJe souhaite un devis de réparation :\n` +
      `- Marque : ${brandLabel}\n` +
      `- Modèle : ${selection.model}\n\n` +
      `Réparations :\n${repairLines.join('\n')}\n\n` +
      `${priceLine}\n\nMerci de me confirmer la disponibilité.`
    );
  };

  const resetAll = () => {
    setStep(1);
    setSelection({ brand: 'apple', model: '', issues: [], screenType: 'fh' });
    setExpandedGroup(null);
  };

  /* ---------- Renderers ---------- */

  const renderBrandTabs = () => (
    <div className="mb-6 text-center" data-testid="brand-tabs-container">
      <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-3">Quelle marque ?</p>
      <div className="inline-flex bg-slate-100 rounded-2xl p-1.5 gap-1 mx-auto" data-testid="brand-tabs">
        {BRANDS.map(brand => (
          <button
            key={brand.key}
            onClick={() => switchBrand(brand.key)}
            className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
              selection.brand === brand.key
                ? `bg-gradient-to-r ${GRADIENT} text-white shadow-md shadow-blue-500/25`
                : 'text-slate-600 hover:text-slate-900 hover:bg-white'
            }`}
            data-testid={`brand-tab-${brand.key}`}
          >
            {brand.label}
          </button>
        ))}
      </div>
      <p className="text-xs text-slate-500 mt-3">Apple, Samsung ou autre marque — on s'adapte.</p>
    </div>
  );

  const renderOtherBrandCard = () => (
    <div className="max-w-xl mx-auto bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200/60 rounded-2xl p-6 text-center" data-testid="other-brand-card">
      <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white">
        <Info size={28} />
      </div>
      <h3 className="text-lg font-bold text-slate-900 mb-2">Devis personnalisé après contact</h3>
      <p className="text-sm text-slate-600 leading-relaxed mb-5 max-w-sm mx-auto">
        Huawei, Xiaomi, Honor, Google Pixel, OnePlus, Oppo… On répare la plupart des marques. Contactez-nous pour un devis précis.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <a href="tel:+33983820754" className="btn-call text-base justify-center" data-testid="other-brand-call">
          <Phone size={18} strokeWidth={2.2} /> Appeler la boutique
        </a>
      </div>
    </div>
  );

  const renderModelGroups = () => {
    const brandName = selection.brand === 'samsung' ? 'Samsung' : 'iPhone';
    return (
      <>
        <h3 className="text-xl font-bold text-slate-900 mb-2 text-center">
          Sélectionnez votre type d'{brandName} puis le modèle
        </h3>
        <p className="text-sm text-slate-500 text-center mb-6">
          Cliquez sur la famille, puis choisissez le modèle exact.
        </p>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 max-w-2xl mx-auto">
          {groups.map(group => {
            const isExpanded = expandedGroup === group.key;
            const selectedInGroup = group.variants ? group.variants.find(v => v === selection.model) : null;
            const isHighlighted = !!selectedInGroup || isExpanded;

            const handleGroupClick = () => {
              if (!group.variants) {
                setSelection({ ...selection, model: 'Autre', issues: [] });
                setExpandedGroup(null);
              } else {
                setExpandedGroup(isExpanded ? null : group.key);
              }
            };

            return (
              <div key={group.key} className={isExpanded ? 'col-span-2 lg:col-span-3' : ''} data-testid={`model-group-${group.key}`}>
                <button
                  onClick={handleGroupClick}
                  className={`relative w-full py-3 px-4 rounded-xl border-2 text-left transition-all duration-300 ${
                    isHighlighted ? `border-transparent bg-gradient-to-br ${BG_GRADIENT} shadow-md` : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                  }`}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="flex flex-col min-w-0">
                      <span className={`font-medium text-sm ${isHighlighted ? 'text-slate-900' : 'text-slate-700'}`}>{group.label}</span>
                      {selectedInGroup && group.variants && (
                        <span className="text-[11px] text-blue-600 font-medium truncate mt-0.5">{selectedInGroup}</span>
                      )}
                    </span>
                    {group.variants ? (
                      <ChevronDown size={16} className={`shrink-0 transition-transform duration-300 ${isExpanded ? 'rotate-180 text-blue-500' : selectedInGroup ? 'text-blue-500' : 'text-slate-400'}`} />
                    ) : (
                      selectedInGroup && (
                        <div className={`shrink-0 w-5 h-5 rounded-full bg-gradient-to-br ${GRADIENT} flex items-center justify-center`}>
                          <Check size={12} className="text-white" />
                        </div>
                      )
                    )}
                  </span>
                </button>

                {isExpanded && group.variants && (
                  <div className="mt-3 p-3 bg-slate-50 rounded-xl grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid={`variants-${group.key}`}>
                    {group.variants.map(variant => (
                      <button
                        key={variant}
                        onClick={() => setSelection({ ...selection, model: variant, issues: [], screenType: 'fh' })}
                        className={`relative py-2.5 px-3 rounded-lg border-2 text-center transition-all ${
                          selection.model === variant ? `border-transparent bg-gradient-to-br ${GRADIENT} shadow-sm` : 'border-white bg-white hover:border-slate-200'
                        }`}
                        data-testid={`model-${variant.replace(/[^a-zA-Z0-9]/g, '-')}`}
                      >
                        <span className={`font-medium text-xs ${selection.model === variant ? 'text-white' : 'text-slate-700'}`}>{variant}</span>
                        {selection.model === variant && <Check size={12} className="inline ml-1 text-white" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-6 max-w-2xl mx-auto flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100" data-testid="other-models-notice">
          <div className="shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white">
            <Info size={18} />
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            <span className="font-semibold text-slate-900">Modèle absent ?</span>{' '}
            <a href="tel:+33983820754" className="font-semibold text-blue-600 hover:text-blue-700 underline underline-offset-2">Appelez la boutique</a>.
          </p>
        </div>
      </>
    );
  };

  const renderAppleIssues = () => {
    if (!modelPrices) return null;
    const screenSelected = selection.issues.includes('screen');
    // Remise pack = prix séparés − prix « avec écran » de la grille (identique en Full HD ou OLED)
    const batterySaving = modelPrices.batteryCombo != null ? modelPrices.screenFH + modelPrices.battery - modelPrices.batteryCombo : 0;
    const vitreSaving = modelPrices.vitreCombo != null ? modelPrices.screenFH + modelPrices.vitre - modelPrices.vitreCombo : 0;

    return (
      <div data-testid="step-2-content">
        <h3 className="text-xl font-bold text-slate-900 mb-2 text-center">Quelles réparations pour votre {selection.model} ?</h3>
        <p className="text-sm text-slate-500 text-center mb-6">Cochez toutes les pannes — les remises s'appliquent automatiquement.</p>

        <div className="grid gap-3 max-w-xl mx-auto">
          {APPLE_REPAIR_TYPES.map(repair => {
            const selected = selection.issues.includes(repair.id);
            const IssueIcon = repair.Icon;

            // Get individual price for display
            let displayPrice = null;
            if (repair.key && modelPrices[repair.key] != null) {
              displayPrice = modelPrices[repair.key];
            } else if (repair.id === 'screen') {
              displayPrice = selection.screenType === 'oled' && hasOled ? modelPrices.screenOLED : modelPrices.screenFH;
            }
            const isOther = repair.id === 'other';

            return (
              <div key={repair.id}>
                <button
                  onClick={() => toggleIssue(repair.id)}
                  className={`relative w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all duration-300 ${
                    selected ? `border-blue-500 bg-gradient-to-br ${BG_GRADIENT} shadow-md` : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                  }`}
                  data-testid={`issue-${repair.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-md border-2 shrink-0 flex items-center justify-center transition-all ${
                      selected ? `bg-gradient-to-br ${GRADIENT} border-transparent` : 'border-slate-300 bg-white'
                    }`}>
                      {selected && <Check size={13} className="text-white" strokeWidth={3} />}
                    </div>
                    <IssueIcon size={20} className={selected ? 'text-blue-600' : 'text-slate-500'} strokeWidth={2} />
                    <span className={`font-medium text-left ${selected ? 'text-slate-900' : 'text-slate-700'}`}>{repair.label}</span>
                  </div>
                  <span className={`font-bold whitespace-nowrap ${
                    isOther ? 'text-slate-500 text-xs font-semibold uppercase tracking-wider' : `text-lg bg-gradient-to-r ${GRADIENT} bg-clip-text text-transparent`
                  }`}>
                    {isOther ? 'Sur devis' : `${displayPrice}€`}
                  </span>
                </button>

                {/* Screen type sub-selection */}
                {repair.id === 'screen' && selected && hasOled && (
                  <div className="mt-2 ml-8 flex gap-2" data-testid="screen-type-selector">
                    <button
                      onClick={() => setSelection(s => ({ ...s, screenType: 'fh' }))}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                        selection.screenType === 'fh'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                      }`}
                      data-testid="screen-type-fh"
                    >
                      {selection.screenType === 'fh' && <Check size={14} className="text-blue-600" />}
                      Full HD — {modelPrices.screenFH}€
                    </button>
                    <button
                      onClick={() => setSelection(s => ({ ...s, screenType: 'oled' }))}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                        selection.screenType === 'oled'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                      }`}
                      data-testid="screen-type-oled"
                    >
                      {selection.screenType === 'oled' && <Check size={14} className="text-blue-600" />}
                      Soft OLED — {modelPrices.screenOLED}€
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Running total */}
        {selection.issues.length > 0 && (
          <div className="mt-5 max-w-xl mx-auto">
            {/* Combo indicators */}
            {screenSelected && selection.issues.includes('battery') && batterySaving > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 mb-2 bg-green-50 border border-green-200/60 rounded-lg text-sm text-green-700">
                <Layers size={16} /> <span className="font-semibold">Pack Écran + Batterie :</span> remise {batterySaving}€ appliquée
              </div>
            )}
            {screenSelected && selection.issues.includes('vitre') && !(selection.issues.includes('battery')) && vitreSaving > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 mb-2 bg-green-50 border border-green-200/60 rounded-lg text-sm text-green-700">
                <Layers size={16} /> <span className="font-semibold">Pack Écran + Vitre :</span> remise {vitreSaving}€ appliquée
              </div>
            )}
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200/60 rounded-xl">
              <span className="text-sm font-semibold text-slate-700">
                {selection.issues.length} réparation{selection.issues.length > 1 ? 's' : ''}
              </span>
              <span className={`text-lg font-bold ${isCustomQuote ? 'text-slate-500 text-sm' : `bg-gradient-to-r ${GRADIENT} bg-clip-text text-transparent`}`}>
                {isCustomQuote ? 'Sur devis' : `${appleResult.total}€${hasCustomQuote ? ' + sur devis' : ''}`}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSamsungIssues = () => (
    <div data-testid="step-2-content">
      <h3 className="text-xl font-bold text-slate-900 mb-2 text-center">Quel est le problème ?</h3>
      <p className="text-sm text-slate-500 text-center mb-6">Cochez toutes les pannes qui s'appliquent.</p>
      <div className="grid gap-3 max-w-xl mx-auto">
        {samsungIssues.map(issue => {
          const selected = selection.issues.includes(issue.id);
          const isOther = issue.price == null;
          const IssueIcon = issue.Icon;
          return (
            <button
              key={issue.id}
              onClick={() => toggleIssue(issue.id)}
              className={`relative flex items-center justify-between p-4 rounded-xl border-2 transition-all duration-300 ${
                selected ? `border-blue-500 bg-gradient-to-br ${BG_GRADIENT} shadow-md` : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
              }`}
              data-testid={`issue-${issue.id}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-md border-2 shrink-0 flex items-center justify-center transition-all ${
                  selected ? `bg-gradient-to-br ${GRADIENT} border-transparent` : 'border-slate-300 bg-white'
                }`}>
                  {selected && <Check size={13} className="text-white" strokeWidth={3} />}
                </div>
                {IssueIcon && <IssueIcon size={20} className={selected ? 'text-blue-600' : 'text-slate-500'} strokeWidth={2} />}
                <span className={`font-medium text-left ${selected ? 'text-slate-900' : 'text-slate-700'}`}>{issue.label}</span>
              </div>
              <span className={`font-bold whitespace-nowrap ${isOther ? 'text-slate-500 text-xs uppercase tracking-wider' : `text-lg bg-gradient-to-r ${GRADIENT} bg-clip-text text-transparent`}`}>
                {isOther ? 'Sur devis' : `${issue.price}€`}
              </span>
            </button>
          );
        })}
      </div>
      {selection.issues.length > 0 && (
        <div className="mt-5 max-w-xl mx-auto flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200/60 rounded-xl">
          <span className="text-sm font-semibold text-slate-700">{selection.issues.length} panne{selection.issues.length > 1 ? 's' : ''}</span>
          <span className={`text-lg font-bold ${samsungHasCustom && samsungTotal === 0 ? 'text-slate-500 text-sm' : `bg-gradient-to-r ${GRADIENT} bg-clip-text text-transparent`}`}>
            {samsungHasCustom && samsungTotal === 0 ? 'Sur devis' : `${samsungTotal}€${samsungHasCustom ? ' + sur devis' : ''}`}
          </span>
        </div>
      )}
    </div>
  );

  const renderQuoteResult = () => {
    const lines = isApple ? appleResult.lines : samsungSelected.map(i => ({ label: i.label, price: i.price, isCombo: false }));

    return (
      <div className="text-center" data-testid="step-3-content">
        <div className={`inline-block bg-gradient-to-br ${BG_GRADIENT} rounded-3xl p-8 mb-8`}>
          {isCustomQuote ? (
            <>
              <span className="text-slate-500 text-lg block mb-2">Devis personnalisé</span>
              <div className="text-3xl sm:text-4xl font-bold text-slate-900">À déterminer après diagnostic</div>
            </>
          ) : (
            <>
              <span className="text-slate-500 text-lg block mb-2">Prix total de la réparation</span>
              <div className={`text-6xl font-bold bg-gradient-to-r ${GRADIENT} bg-clip-text text-transparent`}>
                {totalPrice}€
              </div>
              {hasCustomQuote && <p className="text-xs text-slate-500 mt-2">+ une panne sur devis (à confirmer)</p>}
            </>
          )}
          <div className="flex justify-center gap-3 mt-4">
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-white rounded-full text-sm text-green-700 font-medium">
              <Check size={14} /> Garantie 6 mois
            </span>
          </div>
        </div>

        <div className="bg-slate-50 rounded-2xl p-6 text-left max-w-md mx-auto mb-8">
          <h4 className="font-semibold text-slate-900 mb-4">Récapitulatif</h4>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between py-2 border-b border-slate-200">
              <span className="text-slate-500">Marque</span>
              <span className="text-slate-900 font-medium">{brandLabel}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-200">
              <span className="text-slate-500">Modèle</span>
              <span className="text-slate-900 font-medium">{selection.model}</span>
            </div>
            <div className="py-2">
              <div className="text-slate-500 mb-2">Réparations</div>
              <div className="space-y-1.5">
                {lines.map((l, i) => (
                  <div key={i} className="flex justify-between text-slate-900">
                    <span className="font-medium flex items-center gap-2">
                      {l.isCombo && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-bold">PACK</span>}
                      {l.label}
                    </span>
                    <span className="font-semibold text-slate-700">{l.price != null ? `${l.price}€` : 'sur devis'}</span>
                  </div>
                ))}
              </div>
            </div>
            {!isCustomQuote && (
              <div className="flex justify-between py-3 border-t-2 border-slate-300">
                <span className="text-slate-900 font-bold text-base">Total</span>
                <span className={`font-bold text-lg bg-gradient-to-r ${GRADIENT} bg-clip-text text-transparent`}>{totalPrice}€</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="tel:+33983820754"
            className="btn-call text-base"
            onClick={() => toast.success('Appel en cours...')}
            data-testid="quote-call-cta"
          >
            <Phone size={18} strokeWidth={2.2} />
            {isCustomQuote ? 'Demander un devis' : 'Appeler pour confirmer'}
          </a>
          <button onClick={resetAll} className="px-6 py-3 border-2 border-slate-200 rounded-xl font-semibold text-slate-600 hover:border-slate-300 hover:bg-slate-50 transition-all" data-testid="quote-restart">
            Nouveau devis
          </button>
        </div>
      </div>
    );
  };

  const renderSteps = () => (
    <>
      {step === 1 && (
        <div data-testid="step-1-content">
          <div className="flex justify-center">{renderBrandTabs()}</div>
          {selection.brand === 'other' ? renderOtherBrandCard() : renderModelGroups()}
        </div>
      )}
      {step === 2 && (isApple ? renderAppleIssues() : renderSamsungIssues())}
      {step === 3 && renderQuoteResult()}
    </>
  );

  const renderNav = () => {
    if (step >= 3) return null;
    return (
      <div className="flex justify-between mt-8 pt-6 border-t border-slate-200">
        <button
          onClick={handleBack}
          disabled={step === 1}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
            step === 1 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:bg-slate-100'
          }`}
          data-testid="btn-back"
        >
          <ChevronLeft size={20} /> Retour
        </button>
        <button
          onClick={handleNext}
          disabled={!canProceed()}
          className={`flex items-center gap-2 px-8 py-3 rounded-xl font-semibold transition-all ${
            canProceed() ? `bg-gradient-to-r ${GRADIENT} text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5` : 'bg-slate-100 text-slate-400 cursor-not-allowed'
          }`}
          data-testid="btn-next"
        >
          {step === 2 ? 'Voir mon devis' : 'Continuer'}
          <ChevronRight size={20} />
        </button>
      </div>
    );
  };

  if (embedded) {
    return (
      <div ref={containerRef} data-testid="quote-configurator" style={{ scrollMarginTop: 90 }}>
        <div className="flex items-center justify-center mb-8">
          {STEPS.map((s, index) => (
            <div key={s.num} className="flex items-center">
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                  step > s.num ? 'bg-gradient-to-br from-green-400 to-emerald-500 text-white'
                    : step === s.num ? `bg-gradient-to-br ${GRADIENT} text-white shadow-lg`
                    : 'bg-slate-100 text-slate-400'
                }`}>
                  {step > s.num ? <Check size={18} /> : s.num}
                </div>
                <span className={`text-xs mt-1.5 font-medium ${step >= s.num ? 'text-slate-700' : 'text-slate-500'}`}>{s.label}</span>
              </div>
              {index < STEPS.length - 1 && (
                <div className={`w-20 lg:w-32 h-1 mx-2 rounded-full transition-all ${step > s.num ? 'bg-emerald-400' : 'bg-slate-200'}`} />
              )}
            </div>
          ))}
        </div>
        <div className="min-h-[320px]">{renderSteps()}</div>
        {renderNav()}
      </div>
    );
  }

  return null;
};

export default QuoteConfigurator;
